import { useCallback, useEffect, useState } from "react";
import { LuSend } from "react-icons/lu";
import { MdLinkOff } from "react-icons/md";
import { useAuth } from "../../lib/auth";
import { disconnectTelegram, createConnectUrl, fetchTelegramLink, fetchTelegramAdminSetting } from "../../lib/telegram";
import { getTelegramAppInitData, telegramAppLink } from "../../lib/telegramApp";
import { TelegramAdminSetting, TelegramLink } from "../../models/telegram";

export const TelegramConnectCard = ({ className = "" }: { className?: string }) => {
  const { profile, role, user } = useAuth();  const isStaff = role === "admin" || role === "manager";
  const [telegram, setTelegram] = useState<TelegramLink | TelegramAdminSetting | null>(null);
  const [telegramLoaded, setTelegramLoaded] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [awaitingConnect, setAwaitingConnect] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshTelegram = useCallback(() => {
    if (!profile) return;
    const load = isStaff ? fetchTelegramAdminSetting(profile.id) : fetchTelegramLink();
    return load
      .then((t) => {
        setTelegram(t);
        setTelegramLoaded(true);
      })
      .catch(() => setTelegramLoaded(true));
  }, [isStaff, profile]);

  useEffect(() => {
    if (profile) refreshTelegram();
  }, [profile, refreshTelegram]);

  useEffect(() => {
    if (!awaitingConnect) return;
    const onFocus = () => {
      setAwaitingConnect(false);
      refreshTelegram();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [awaitingConnect, refreshTelegram]);

  const handleConnect = async () => {
    setConnecting(true);
    setError(null);
    try {
      const initData = getTelegramAppInitData();

      let linked = false;
      if (initData && user) {
        try {
          await telegramAppLink(initData);
          linked = true;
        } catch {
        }
      }
      if (linked) {
        await refreshTelegram();
      } else {
        const url = await createConnectUrl();
        window.open(url, "_blank", "noopener");
        setAwaitingConnect(true);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Помилка, спробуйте ще раз");
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!telegram) return;
    setError(null);
    try {
      await disconnectTelegram(
        isStaff, isStaff
          ? (telegram as TelegramAdminSetting).id
          : (telegram as TelegramLink).user_id
      );
      setTelegram(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Помилка, спробуйте ще раз");
    }
  };

  if (!telegramLoaded) return null;

  return telegram ? (
    <div className={`rounded-lg bg-white/5 p-4 ${className}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm">
          <p className="font-semibold text-white">Telegram підключено</p>
          {telegram.telegram_username && (
            <p className="text-green-500">@{telegram.telegram_username}</p>
          )}
        </div>
        <button
          type="button"
          onClick={handleDisconnect}
          className="text-neutral-300 hover:text-red-500 font-medium text-lg"
        ><MdLinkOff /></button>
      </div>
      {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
    </div>
  ) : (
    <div className={`rounded-lg bg-white/5 p-4 ${className}`}>
      <div className="flex items-start gap-3">
        <LuSend className="shrink-0 text-lg text-neutral-400 mt-1" aria-hidden />
        <div className="text-sm">
          <p className="font-semibold text-white">
            Telegram не підключено
          </p>
          <p className="text-neutral-400">
            Отримуйте сповіщення про проєкти
          </p>
        </div>
      </div>
      {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
      <button
        type="button"
        onClick={handleConnect}
        disabled={connecting}
        className="btn-brand mt-3 w-full justify-center px-4 py-2.5 text-xs disabled:opacity-50"
      >
        {connecting ? "Підключення…" : "Підключити Telegram"}
      </button>
    </div>
  );
};
